/**
 * Pure helpers for CSV import "map a new name to an existing record" choices.
 *
 * A mapping is a plain object of { "<name from the file>": <existing id> | null }.
 * A name that is absent means "create it as a new record". A value of `null`
 * means "leave the products with this name without one" (see SKIP).
 */

/** Dropdown value meaning "leave without". Stored as `null` in the map. */
export const SKIP = "__skip__";

/**
 * Returns a new map with `name` pointed at `id`. A blank, null or undefined
 * id removes the entry, i.e. goes back to "create new". SKIP stores null,
 * i.e. "leave without".
 */
export function setChoice(map, name, id) {
  const next = { ...map };
  if (id === SKIP) {
    next[name] = null;
  } else if (id === "" || id === null || id === undefined) {
    delete next[name];
  } else {
    next[name] = Number(id);
  }
  return next;
}

/**
 * The extra multipart field for one mapping. Empty when nothing is mapped, so
 * the API sees no such field at all.
 */
export function mapToFields(fieldName, map) {
  return Object.keys(map).length === 0
    ? {}
    : { [fieldName]: JSON.stringify(map) };
}

/**
 * Active mappings as display rows, sorted by the name from the file. `options`
 * is [{ id, label }]. A mapping whose target is unknown still shows, with the
 * id as a fallback label. A skipped name shows with `skipped: true` and the
 * given `skipLabel`.
 */
export function describeMappings(map, options, skipLabel = "left without") {
  const labelsById = new Map(
    (options || []).map((option) => [Number(option.id), option.label])
  );
  return Object.entries(map)
    .map(([name, id]) =>
      id === null
        ? { name, id: null, label: skipLabel, skipped: true }
        : {
            name,
            id,
            label: labelsById.get(Number(id)) ?? `#${id}`,
            skipped: false,
          }
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}
